import { fileTypeFromBlob } from 'file-type';
import logger from './logger';

/**
 * Custom magic byte signatures for file types not supported by the `file-type` library.
 * Each entry defines how to identify a file format from its raw bytes.
 */
const CUSTOM_SIGNATURES = [
    {
        ext: 'hdf5',
        mime: 'application/x-hdf5',
        // HDF5 files start with \x89HDF\r\n\x1a\n
        detect: (buffer) => {
            if (buffer.length < 8) return false;
            return (
                buffer[0] === 0x89 &&
                buffer[1] === 0x48 && // H
                buffer[2] === 0x44 && // D
                buffer[3] === 0x46 && // F
                buffer[4] === 0x0D && // \r
                buffer[5] === 0x0A && // \n
                buffer[6] === 0x1A &&
                buffer[7] === 0x0A    // \n
            );
        }
    },

];

const FILE_TYPE_EXTENSION_ALIASES = {
    sqlite: 'db'
};

/**
 * Attempts to detect file type using custom magic byte signatures.
 * @param {ArrayBuffer|Uint8Array} buffer - File header bytes
 * @returns {{ ext: string, mime: string } | null} Detected type or null
 */
const detectCustomType = (buffer) => {
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    for (const sig of CUSTOM_SIGNATURES) {
        if (sig.detect(bytes)) {
            return { ext: sig.ext, mime: sig.mime };
        }
    }
    return null;
};

/**
 * Attempts to detect text-based file types (scripts, XML, CSV) by reading
 * the initial content as UTF-8 text and applying heuristics.
 * Script detection runs FIRST so dangerous script types are identified
 * before falling through to allowed text formats.
 * @param {ArrayBuffer|Uint8Array} buffer - File header bytes
 * @returns {{ ext: string, mime: string } | null} Detected type or null
 */
const detectTextBasedType = (buffer) => {
    try {
        const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);

        // Skip UTF-8 BOM if present
        let start = 0;
        if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
            start = 3;
        }

        const decoder = new TextDecoder('utf-8', { fatal: true });
        const text = decoder.decode(bytes.slice(start, Math.min(bytes.length, 4096))).trim();

        if (!text) return null;

        // --- Script/dangerous type detection (must run before XML/CSV) ---

        // Shebang detection: #!<interpreter>
        if (text.startsWith('#!')) {
            const firstLine = text.split(/\r?\n/)[0].toLowerCase();

            // Shell scripts: bash, sh, zsh, ksh, csh, fish, dash
            if (/\b(bash|sh|zsh|ksh|csh|fish|dash)\b/.test(firstLine)) {
                return { ext: 'sh', mime: 'application/x-sh' };
            }
            // Python scripts
            if (/\bpython/.test(firstLine)) {
                return { ext: 'py', mime: 'text/x-python' };
            }
            // Node.js / JavaScript
            if (/\bnode\b/.test(firstLine)) {
                return { ext: 'js', mime: 'application/javascript' };
            }
            // Perl
            if (/\bperl\b/.test(firstLine)) {
                return { ext: 'pl', mime: 'text/x-perl' };
            }
            // Ruby
            if (/\bruby\b/.test(firstLine)) {
                return { ext: 'rb', mime: 'application/x-ruby' };
            }
            // Generic shebang — report as shell script (most likely case)
            return { ext: 'sh', mime: 'application/x-sh' };
        }

        // Batch file detection: @echo off, rem, or common batch patterns at the start
        if (/^@echo\s+off/i.test(text) || /^rem\s/i.test(text)) {
            return { ext: 'bat', mime: 'application/x-bat' };
        }

        // PowerShell detection: common PowerShell patterns
        if (/^(param\s*\(|\$[A-Za-z]|function\s+[A-Za-z]|#\s*\.SYNOPSIS)/m.test(text) &&
            /\b(Get-|Set-|New-|Remove-|Invoke-|Write-Host|Write-Output|\$PSVersionTable|\[CmdletBinding)/m.test(text)) {
            return { ext: 'ps1', mime: 'application/x-powershell' };
        }

        // VBScript detection
        if (/^(Option\s+Explicit|Dim\s+|Sub\s+|Function\s+|WScript\.|CreateObject\()/im.test(text)) {
            return { ext: 'vbs', mime: 'application/x-vbs' };
        }

        // --- Allowed text format detection ---

        // XML detection: starts with "<?xml" or a root element tag
        if (text.startsWith('<?xml') || /^<[a-zA-Z][^>]*>/.test(text)) {
            return { ext: 'xml', mime: 'application/xml' };
        }

        // CSV detection heuristic:
        // - Multiple lines with consistent comma-separated values
        // - At least 2 rows and 2 columns
        const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
        if (lines.length >= 2) {
            const commaCountFirst = (lines[0].match(/,/g) || []).length;
            if (commaCountFirst >= 1) {
                // Check that at least 60% of subsequent lines have a similar comma count
                let consistent = 0;
                const checkLines = Math.min(lines.length, 10);
                for (let i = 1; i < checkLines; i++) {
                    const count = (lines[i].match(/,/g) || []).length;
                    if (count >= commaCountFirst - 1 && count <= commaCountFirst + 1) {
                        consistent++;
                    }
                }
                if (consistent / (checkLines - 1) >= 0.6) {
                    return { ext: 'csv', mime: 'text/csv' };
                }
            }
        }

        return null;
    } catch {
        // Decoding failed — file is not valid UTF-8 text
        return null;
    }
};

/**
 * Detects file type from file content using magic number detection.
 * Reads magic bytes for EVERY file (not just extensionless ones).
 *
* Detection order:
 * 1. Custom signatures for formats not supported by `file-type` (HDF5)
 * 2. `file-type` library for supported binary formats
 * 3. Text-based heuristics for scripts, XML, and CSV
 *
 * Supported file types that can be inferred:
 * - pdf, png, zip, tar, 7z, mp4, avi, xlsx, dcm, and parquet
 *   (via `file-type`)
 * - db (SQLite detected by `file-type` as `sqlite` and normalized to `db`)
 * - hdf5 (custom HDF5 signature)
 * - xml (text starts with "<?xml" or a root element)
 * - csv (text with consistent comma-delimited rows)
 * - sh (shell shebang)
 * - py (Python shebang)
 * - js (Node.js shebang)
 * - bat (starts with @echo off or rem)
 * - ps1 (PowerShell patterns)
 * - vbs (VBScript patterns)
 *
 * File types that CANNOT be inferred (no distinctive magic bytes):
 * - txt, hea, dat, raw, vol, 4dv, testdevs
 * For these, the system falls back to the filename extension.
 *
 * @param {File|Blob} file - The File/Blob object to analyze
 * @returns {Promise<string>} - The detected extension (e.g., 'dcm', 'pdf', 'png') or empty string if unknown
 */
export const detectFileType = async (file) => {
    try {
        // Read the first 4100 bytes for binary signatures and text heuristics.
        const blob = file.slice(0, 4100);
        let buffer = null;

        // Attempt header-byte reads for custom/text detection, but do not fail hard if unavailable.
        try {
            if (typeof blob.arrayBuffer === 'function') {
                const arrayBuffer = await blob.arrayBuffer();
                buffer = new Uint8Array(arrayBuffer);

                //Try signatures for formats not supported by file-type.
                const customResult = detectCustomType(buffer);
                if (customResult) {
                    logger.info(`🔍 Detected file type for "${file.name}": ${customResult.ext} (${customResult.mime}) [custom]`);
                    return customResult.ext;
                }
            }
        } catch (bufferError) {
            logger.debug(`🔍 Could not read header bytes for "${file.name}"; continuing with library detection`, bufferError);
        }

        // 2. Try file-type library (handles most common binary formats)
        const type = await fileTypeFromBlob(blob);
        if (type?.ext) {
            const detectedExtension = type.ext.toLowerCase();
            const extension =
                FILE_TYPE_EXTENSION_ALIASES[detectedExtension] ?? detectedExtension;

            logger.info(
                `🔍 Detected file type for "${file.name}": ${extension} (${type.mime}) [file-type]`
            );
            return extension;
        }

        // 3. Try text-based heuristics (XML, CSV)
        if (buffer) {
            const textResult = detectTextBasedType(buffer);
            if (textResult) {
                logger.info(`🔍 Detected file type for "${file.name}": ${textResult.ext} (${textResult.mime}) [text-heuristic]`);
                return textResult.ext;
            }
        }

        // Unknown file type — return empty string
        logger.debug(`🔍 Could not detect file type for "${file.name}", returning empty string`);
        return '';

    } catch (error) {
        // If detection fails (error reading file, etc.), return empty string
        logger.warn(`⚠️ Error detecting file type for "${file.name}":`, error);
        return '';
    }
};

/**
 * Batch detect file types for multiple files.
 * Useful for processing many files efficiently.
 *
 * @param {File[]} files - Array of File objects to analyze
 * @returns {Promise<Map<string, string>>} - Map of file.name to detected extension
 */
export const batchDetectFileTypes = async (files) => {
    const detectionMap = new Map();

    const promises = files.map(async (file) => {
        const extension = await detectFileType(file);
        detectionMap.set(file.name, extension);
    });

    await Promise.all(promises);
    return detectionMap;
};
